// Regiebericht math, client twin of utils/workReports/calc.js (live totals while editing; the
// server stays authoritative). Keep everything below the header identical to the server file:
// test/workReports/calc.test.js compares both.

const LABOUR_KINDS = ["WORK", "TRAVEL"];
const MATERIAL_KINDS = ["MATERIAL", "FLAT", "EQUIPMENT"];
const STATUSES = ["draft", "signed", "refused", "invoiced"];
// Billing increments a company may round each worker's time up to (0 = exact minutes).
const ROUNDING_OPTIONS = [0, 5, 6, 10, 15, 30, 60];
const TAX_RATES = [19, 7, 0];
const MAX_LINES = 200;
const MINUTES_PER_DAY = 1440;
// A unit price above 1 million euros or a quantity above 1 million is a typo.
const MAX_CENTS = 100000000;
const MAX_QUANTITY = 1000000;

/** Integer a / b rounded half away from zero (b > 0). */
const divRound = (a, b) => {
  const sign = a < 0 ? -1 : 1;
  return sign * Math.floor((Math.abs(a) * 2 + b) / (2 * b));
};

const toInt = (value, { min = 0, max = Number.MAX_SAFE_INTEGER } = {}) => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
};

/** "7:30", "07:30", "0730", "7.30" or "7" → minutes after midnight; anything else → null. */
const parseClock = value => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (!text) return null;
  let match = text.match(/^(\d{1,2})[:.,h](\d{2})$/);
  if (!match) match = text.match(/^(\d{1,2})(\d{2})$/);
  if (!match) match = text.match(/^(\d{1,2})$/) && [text, text, "00"];
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 24 || minutes > 59 || (hours === 24 && minutes > 0)) return null;
  return hours * 60 + minutes;
};

/** Minutes after midnight → "HH:mm". */
const formatClock = minutes => {
  if (minutes === null || minutes === undefined || !Number.isFinite(Number(minutes))) return "";
  const m = ((Math.round(Number(minutes)) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

/** Minutes between two clock times; an end before the start runs past midnight. */
const spanMinutes = (start, end) => {
  const from = parseClock(start);
  const to = parseClock(end);
  if (from === null || to === null) return null;
  const span = to - from;
  return span < 0 ? span + MINUTES_PER_DAY : span;
};

/** Worked time rounded up to the company's billing increment. */
const billedMinutes = (minutes, rounding) => {
  const step = ROUNDING_OPTIONS.includes(Number(rounding)) ? Number(rounding) : 0;
  if (!step || minutes <= 0) return Math.max(0, minutes);
  return Math.ceil(minutes / step) * step;
};

/** Minutes → hours in hundredths (45 min → 75, i.e. 0.75 h), the invoice quantity. */
const hundredthsOfHours = minutes => divRound(Math.round(minutes) * 100, 60);

/** Quantity with up to 3 decimals → thousandths (1.25 → 1250). */
const thousandths = quantity => Math.round(Number(quantity || 0) * 1000);

/** Keeps what a labour line may hold, in the stored format. */
const normalizeLabour = (raw = {}) => {
  const line = raw || {};
  const start = parseClock(line.start);
  const end = parseClock(line.end);
  const out = {
    kind: LABOUR_KINDS.includes(line.kind) ? line.kind : "WORK",
    name: String(line.name || "")
      .trim()
      .slice(0, 120),
    employee: line.employee || null,
    start: start === null ? "" : formatClock(start),
    end: end === null ? "" : formatClock(end),
    breakMinutes: toInt(line.breakMinutes, { max: MINUTES_PER_DAY }),
    // Without start and end, the duration is typed directly.
    minutes: toInt(line.minutes, { max: MINUTES_PER_DAY }),
    rate: toInt(line.rate, { max: MAX_CENTS }),
    note: String(line.note || "")
      .trim()
      .slice(0, 300),
    source: line.source === "ATTENDANCE" ? "ATTENDANCE" : "MANUAL"
  };
  if (line._id) out._id = line._id;
  if (out.start && out.end) {
    out.minutes = Math.max(0, spanMinutes(out.start, out.end) - out.breakMinutes);
  }
  return out;
};

/** Keeps what a material line may hold, in the stored format. */
const normalizeMaterial = (raw = {}) => {
  const line = raw || {};
  const quantity = Number(line.quantity);
  const out = {
    kind: MATERIAL_KINDS.includes(line.kind) ? line.kind : "MATERIAL",
    description: String(line.description || "")
      .trim()
      .slice(0, 300),
    quantity: Number.isFinite(quantity)
      ? Math.min(MAX_QUANTITY, Math.max(0, thousandths(quantity) / 1000))
      : 0,
    unit: String(line.unit || "")
      .trim()
      .slice(0, 20),
    unitPrice: toInt(line.unitPrice, { max: MAX_CENTS }),
    articleNumber: String(line.articleNumber || "")
      .trim()
      .slice(0, 60)
  };
  if (line._id) out._id = line._id;
  return out;
};

const normalizeSettings = (raw = {}) => {
  const s = raw || {};
  return {
    hourlyRate: toInt(s.hourlyRate, { max: MAX_CENTS }),
    travelRate: toInt(s.travelRate, { max: MAX_CENTS }),
    rounding: ROUNDING_OPTIONS.includes(Number(s.rounding)) ? Number(s.rounding) : 0,
    taxRate: TAX_RATES.includes(Number(s.taxRate)) ? Number(s.taxRate) : 19,
    showPrices: s.showPrices === undefined ? true : Boolean(s.showPrices),
    emailCopy: s.emailCopy === undefined ? true : Boolean(s.emailCopy),
    terms: String(s.terms || "").slice(0, 2000)
  };
};

/** Ranges [from, to) in minutes of the day of a timed line; past midnight wraps into a 2nd day. */
const rangeOf = line => {
  const from = parseClock(line.start);
  const span = spanMinutes(line.start, line.end);
  if (from === null || span === null) return null;
  return [from, from + span];
};

/**
 * Totals of a report: per line minutes, billed minutes, hours (hundredths) and net cents; sums
 * of work/travel time, labour, materials, VAT; and warnings for things to check before signing.
 */
const summarizeReport = (report = {}, settingsRaw = {}) => {
  const settings = normalizeSettings(settingsRaw);
  const rounding = report.rounding === undefined ? settings.rounding : report.rounding;
  const taxRate = TAX_RATES.includes(Number(report.taxRate))
    ? Number(report.taxRate)
    : settings.taxRate;
  const warnings = [];
  const labour = (report.labour || []).map((raw, index) => {
    const line = normalizeLabour(raw);
    const billed = billedMinutes(line.minutes, rounding);
    const hours = hundredthsOfHours(billed);
    const net = divRound(hours * line.rate, 100);
    if (!line.minutes) warnings.push({ code: "NO_TIME", line: index });
    if (!line.rate) warnings.push({ code: "NO_RATE", line: index });
    if (!line.name && !line.employee) warnings.push({ code: "NO_NAME", line: index });
    return { ...line, billedMinutes: billed, hours, net };
  });
  // The same person cannot be in two places at once.
  const byPerson = new Map();
  labour.forEach((line, index) => {
    const key = line.employee ? `e:${line.employee}` : line.name ? `n:${line.name}` : "";
    const range = rangeOf(line);
    if (!key || !range) return;
    for (const other of byPerson.get(key) || []) {
      if (range[0] < other.range[1] && other.range[0] < range[1]) {
        warnings.push({ code: "OVERLAP", line: index, other: other.index });
      }
    }
    byPerson.set(key, [...(byPerson.get(key) || []), { index, range }]);
  });
  const materials = (report.materials || []).map((raw, index) => {
    const line = normalizeMaterial(raw);
    const net = divRound(thousandths(line.quantity) * line.unitPrice, 1000);
    if (!line.description) warnings.push({ code: "NO_DESCRIPTION", material: index });
    if (!line.quantity) warnings.push({ code: "NO_QUANTITY", material: index });
    return { ...line, net };
  });
  const sum = (list, pick) => list.reduce((total, item) => total + pick(item), 0);
  const labourNet = sum(labour, l => l.net);
  const materialNet = sum(materials, m => m.net);
  const net = labourNet + materialNet;
  const tax = divRound(net * taxRate, 100);
  if (!labour.length && !materials.length) warnings.push({ code: "EMPTY" });
  return {
    labour,
    materials,
    rounding,
    taxRate,
    warnings,
    totals: {
      workMinutes: sum(
        labour.filter(l => l.kind === "WORK"),
        l => l.minutes
      ),
      travelMinutes: sum(
        labour.filter(l => l.kind === "TRAVEL"),
        l => l.minutes
      ),
      billedMinutes: sum(labour, l => l.billedMinutes),
      labourNet,
      materialNet,
      net,
      tax,
      gross: net + tax
    }
  };
};

export {
  LABOUR_KINDS,
  MATERIAL_KINDS,
  STATUSES,
  ROUNDING_OPTIONS,
  TAX_RATES,
  MAX_LINES,
  divRound,
  parseClock,
  formatClock,
  spanMinutes,
  billedMinutes,
  hundredthsOfHours,
  normalizeLabour,
  normalizeMaterial,
  normalizeSettings,
  summarizeReport
};
